import { useState, useEffect } from 'react';
import { Box, Typography, useTheme, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import { getUnits, convertDistance, formatSpeed } from '../../utils/units';

const CLIMB_COLORS = {
  'B-C4': '#ffe066',
  'B-C3': '#ffa020',
  'B-C2': '#e05000',
  'B-C1': '#c01010',
  'B-HC': '#7a0000',
};

const DESCENT_COLORS = {
  'B-D4': '#38bdf8',
  'B-D3': '#3b82f6',
  'B-D2': '#6366f1',
  'B-D1': '#7c3aed',
  'B-DHC': '#9333ea',
};

const ZONE_COLORS = {
  Z1: '#9ca3af',
  Z2: '#22c55e',
  Z3: '#eab308',
  Z4: '#f97316',
  Z5: '#ef4444',
  Z6: '#a855f7',
};

const formatDuration = (secs) => {
  if (secs == null || secs <= 0) return '-';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
};

const formatElapsed = (secs) => {
  if (secs == null) return '-';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
};

const TypeChip = ({ row, colors }) => {
  const seg = row.segment_type;
  const detail = row.segment_detail || '';
  const cat = detail.split(' ')[0];

  if (seg === 'climb') {
    return (
      <Chip
        label={detail || 'Climb'}
        size="small"
        sx={{
          backgroundColor: CLIMB_COLORS[cat] || '#ffa020',
          color: cat === 'B-C4' ? '#3a2800' : '#fff',
          fontWeight: 'bold',
          fontSize: '0.65rem',
          height: 20,
        }}
      />
    );
  }
  if (seg === 'descent') {
    return (
      <Chip
        label={detail || 'Descent'}
        size="small"
        sx={{
          backgroundColor: DESCENT_COLORS[cat] || '#3b82f6',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '0.65rem',
          height: 20,
        }}
      />
    );
  }
  return (
    <Chip
      label="Rolling"
      size="small"
      sx={{ backgroundColor: colors.grey[700], color: colors.grey[300], fontSize: '0.65rem', height: 20 }}
    />
  );
};

const ZoneChip = ({ zone, label }) => (
  <Chip
    label={label || zone || '—'}
    size="small"
    sx={{
      backgroundColor: ZONE_COLORS[zone] ? ZONE_COLORS[zone] + '33' : '#374151',
      color: ZONE_COLORS[zone] || '#9ca3af',
      border: `1px solid ${ZONE_COLORS[zone] || '#9ca3af'}`,
      fontWeight: 'bold',
      fontSize: '0.6rem',
      height: 18,
    }}
  />
);

const BikePlanGrid = ({ segments = [], onSegmentSelect = null, onSegmentDblClick = null }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [selectedId, setSelectedId] = useState(null);
  const [units, setUnits] = useState(getUnits());

  useEffect(() => {
    const handler = (e) => setUnits(e.detail || getUnits());
    window.addEventListener('unitsChanged', handler);
    return () => window.removeEventListener('unitsChanged', handler);
  }, []);

  const rows = segments.map((seg, idx) => ({ id: idx, ...seg }));

  const columns = [
    {
      field: 'start_label',
      headerName: 'Segment',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box display="flex" flexDirection="column" justifyContent="center" py={0.5}>
            <Typography variant="caption" color={colors.grey[400]} lineHeight={1.2}>
              {row.start_label}
            </Typography>
            <Typography variant="body2" color={colors.grey[100]} fontWeight="bold" lineHeight={1.2}>
              {row.end_label}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'segment_type',
      headerName: 'Type',
      width: 110,
      renderCell: (params) => <TypeChip row={params.row} colors={colors} />,
    },
    {
      field: 'start_distance_m',
      headerName: units === 'imperial' ? 'Start mi' : 'Start km',
      width: 75,
      renderCell: (params) => (
        <Typography variant="body2" color={colors.grey[400]}>
          {convertDistance(params.value, units).toFixed(1)}
        </Typography>
      ),
    },
    {
      field: 'end_distance_m',
      headerName: units === 'imperial' ? 'End mi' : 'End km',
      width: 75,
      renderCell: (params) => (
        <Typography variant="body2" color={colors.grey[100]}>
          {convertDistance(params.value, units).toFixed(1)}
        </Typography>
      ),
    },
    {
      field: 'length_m',
      headerName: 'Length',
      width: 80,
      renderCell: (params) => (
        <Typography variant="body2" color={colors.grey[300]}>
          {convertDistance(params.value, units).toFixed(1)} {units === 'imperial' ? 'mi' : 'km'}
        </Typography>
      ),
    },
    {
      field: 'elevation_gain_m',
      headerName: 'Gain',
      width: 65,
      renderCell: (params) => {
        if (!params.value) return null;
        return (
          <Typography variant="body2" color="#ef9a9a">
            +{Math.round(params.value)}m
          </Typography>
        );
      },
    },
    {
      field: 'elevation_loss_m',
      headerName: 'Loss',
      width: 65,
      renderCell: (params) => {
        if (!params.value) return null;
        return (
          <Typography variant="body2" color="#80cbc4">
            -{Math.round(params.value)}m
          </Typography>
        );
      },
    },
    {
      field: 'elapsed_s',
      headerName: 'Elapsed',
      width: 85,
      renderCell: (params) => (
        <Typography variant="body2" color={params.value != null ? '#a5d6a7' : colors.grey[500]}>
          {formatElapsed(params.value)}
        </Typography>
      ),
    },
    {
      field: 'duration_s',
      headerName: 'Duration',
      width: 90,
      renderCell: (params) => (
        <Typography variant="body2" color={colors.grey[100]}>
          {formatDuration(params.value)}
        </Typography>
      ),
    },
    {
      field: 'avg_speed',
      headerName: 'Speed',
      width: 80,
      renderCell: (params) => (
        <Typography variant="body2" color={params.value ? '#60a5fa' : colors.grey[500]}>
          {params.value ? formatSpeed(params.value, units) : '—'}
        </Typography>
      ),
    },
    {
      field: 'avg_power',
      headerName: 'Power',
      width: 80,
      renderCell: (params) => (
        <Typography variant="body2" color={params.value ? '#f59e0b' : colors.grey[500]}>
          {params.value ? `${Math.round(params.value)} W` : '—'}
        </Typography>
      ),
    },
    {
      field: 'vam',
      headerName: 'VAM',
      width: 80,
      renderCell: (params) => (
        <Typography variant="body2" color={params.value ? '#ef9a9a' : colors.grey[500]}>
          {params.value ? `${Math.round(params.value)} m/h` : '—'}
        </Typography>
      ),
    },
    {
      field: 'watts_per_kg',
      headerName: 'W/kg',
      width: 70,
      renderCell: (params) => (
        <Typography variant="body2" color={params.value ? '#a5d6a7' : colors.grey[500]}>
          {params.value ? params.value.toFixed(1) : '—'}
        </Typography>
      ),
    },
    {
      field: 'power_zone',
      headerName: 'Zone',
      width: 100,
      renderCell: (params) => {
        const row = params.row;
        if (!params.value) return null;
        return <ZoneChip zone={params.value} label={row.power_zone_label} />;
      },
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h6" color={colors.grey[100]}>Bike Plan</Typography>
          <Typography variant="body2" color={colors.grey[300]}>{rows.length} segments</Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Chip label="Climb"   size="small" sx={{ backgroundColor: '#ffa020', color: '#fff', fontSize: '0.7rem' }} />
          <Chip label="Descent" size="small" sx={{ backgroundColor: '#3b82f6', color: '#fff', fontSize: '0.7rem' }} />
          <Chip label="Rolling" size="small" sx={{ backgroundColor: '#546e7a', color: '#fff', fontSize: '0.7rem' }} />
        </Box>
      </Box>

      <Box
        sx={{
          height: Math.min(120 + rows.length * 52, 900),
          width: '100%',
          '& .MuiDataGrid-root': { border: 'none', backgroundColor: colors.grey[800] },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${colors.grey[700]}`,
            color: colors.grey[100],
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.grey[900],
            borderBottom: `2px solid ${colors.grey[700]}`,
          },
          '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold', color: colors.grey[100] },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${colors.grey[700]}`,
            backgroundColor: colors.grey[900],
          },
          '& .MuiDataGrid-row': { '&:hover': { backgroundColor: colors.grey[700] } },
          '& .row-climb': {
            backgroundColor: '#1a1000',
            '&:hover': { backgroundColor: '#281900' },
          },
          '& .row-descent': {
            backgroundColor: '#00111a',
            '&:hover': { backgroundColor: '#001c2b' },
          },
          '& .row-selected': {
            outline: '2px solid #ffeb3b',
            outlineOffset: '-2px',
            backgroundColor: '#1a1800 !important',
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={100}
          rowsPerPageOptions={[50, 100, 200]}
          disableRowSelectionOnClick
          hideFooter
          rowHeight={52}
          onRowClick={(params) => {
            const seg = params.row;
            const newId = seg.id === selectedId ? null : seg.id;
            setSelectedId(newId);
            if (onSegmentSelect) onSegmentSelect(newId === null ? null : seg);
          }}
          onRowDoubleClick={(params) => {
            if (onSegmentDblClick) onSegmentDblClick(params.row);
          }}
          getRowClassName={(params) => {
            const t = params.row.segment_type;
            const sel = params.row.id === selectedId ? ' row-selected' : '';
            if (t === 'climb')   return `row-climb${sel}`;
            if (t === 'descent') return `row-descent${sel}`;
            return sel.trim();
          }}
          sx={{ cursor: 'pointer' }}
        />
      </Box>
    </Box>
  );
};

export default BikePlanGrid;
